var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@material-ui/core';
import { ConfigFieldControl, ConfigSortableList, CoreEvent } from '@sierralabs/core-ui';
import { createEntityData, EntityColumnPicker, EntityPicker, useEntityContext } from '@sierralabs/entity-ui';
import { Form, FormProvider } from '@sierralabs/forms-ui';
import { StateNode } from '@sierralabs/state-machine';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteEvent } from '../../hooks/event/useSiteEvent';
import { useSiteContext } from '../../SiteContext';
import SiteEventConfigurator from '../events/SiteEventConfigurator';
import { SiteComponent } from '../SiteComponentPlugin';
const StyledDialog = styled(Dialog) `
  /* z-index: 10000 !important; */
`;
export var SiteCreateEntityDialogEvent;
(function (SiteCreateEntityDialogEvent) {
    SiteCreateEntityDialogEvent["AfterCreate"] = "create-entity-dialog:after-create";
})(SiteCreateEntityDialogEvent || (SiteCreateEntityDialogEvent = {}));
export const SiteCreateEntityDialog = props => {
    var _a;
    const { $openDialogEvent } = props;
    const history = useHistory();
    const { entities } = useEntityContext();
    const { editing, isHover, hoverProps, setHoverComponent } = useSiteContext();
    const component = useSubscribeComponentChanges(SiteCreateEntityDialogPlugin, props);
    const { emit } = useSiteEvent(component);
    const formContextRef = useRef();
    const [formValues, setFormValues] = useState({});
    // handle creating the entity data
    const [createState, setCreateState] = useState({ isLoading: false });
    const { isLoading } = createState;
    const { entityId, formElements, routePath } = component.props || {};
    const isHovering = useMemo(() => {
        return isHover(component);
    }, [component, isHover]);
    const entity = useMemo(() => entities === null || entities === void 0 ? void 0 : entities.find(e => e.id && e.id === entityId), [entities, entityId]);
    // generate the form state node for the form state machine based on the
    // formElements configured by the plugin
    const formStateNode = useMemo(() => {
        // refreshTokenRef.current = refreshTokenRef.current;
        return {
            name: 'Form',
            states: [
                {
                    name: 'Create Entity',
                    meta: {
                        hideTitle: true,
                        elements: formElements
                    }
                }
            ]
        };
    }, [formElements]);
    // update the form's state machine if form elements change
    useEffect(() => {
        if (formContextRef.current) {
            formContextRef.current.trackChanges.setFinal(formStateNode);
            const stateMachine = formContextRef.current.stateMachine;
            if (stateMachine) {
                // this will notify the form to restart
                stateMachine.stateNode = new StateNode(stateMachine, formStateNode);
                stateMachine.start();
            }
        }
    }, [formStateNode]);
    // check if the form is valid
    const formValidated = useMemo(() => !(formElements === null || formElements === void 0 ? void 0 : formElements.some(element => element.dataSource &&
        typeof formValues[element.dataSource] === 'undefined')), [formElements, formValues]);
    const handleClick = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!entity) {
            return;
        }
        setCreateState({ isLoading: true });
        const { result } = createEntityData(entity.tableName, formValues);
        const { data, error } = yield result;
        setCreateState({ isLoading: false, error });
        // skip if no entity data created
        if (!data) {
            return;
        }
        // emit close dialog and after create events
        emit('onDialogClose');
        const eventData = $openDialogEvent === null || $openDialogEvent === void 0 ? void 0 : $openDialogEvent.data;
        CoreEvent.dispatch(SiteCreateEntityDialogEvent.AfterCreate, { component, data, eventData });
        // if not disabled, redirect user after creating entity
        if (routePath && !(eventData === null || eventData === void 0 ? void 0 : eventData.disableRouteRedirect)) {
            history.push(routePath.replace(':id', data.id));
        }
    });
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteCreateEntityDialogPlugin }, props),
        React.createElement(StyledDialog, { open: true, className: 'site-create-entity-dialog', fullWidth: true, PaperProps: Object.assign({ onMouseOver: () => editing && !isHovering && setHoverComponent(component), onMouseOut: () => editing && isHovering && setHoverComponent(undefined) }, hoverProps(component)), onClose: () => !editing && emit('onDialogClose') },
            React.createElement(DialogTitle, null, ((_a = component.props) === null || _a === void 0 ? void 0 : _a.title) || ''),
            React.createElement(DialogContent, null,
                React.createElement(FormProvider, { entitySchema: entity, formStateNode: formStateNode },
                    React.createElement(Form, { disableInlineSave: true, values: formValues, onInit: context => (formContextRef.current = context), onChange: values => {
                            // console.log('form onChange', values);
                            setFormValues(values);
                        } }))),
            React.createElement(DialogActions, null,
                React.createElement(Button, { color: 'primary', onClick: () => !editing && emit('onDialogClose') }, "Cancel"),
                React.createElement(Button, { variant: 'contained', color: 'primary', disabled: editing || isLoading || (!formValidated && !(entity === null || entity === void 0 ? void 0 : entity.tableName)), endIcon: isLoading && React.createElement(CircularProgress, { size: 16 }), onClick: handleClick }, "Create")))));
};
export const SiteCreateEntityDialogConfigurator = props => {
    var _a, _b, _c, _d, _e;
    const { component, update, insert, move, remove } = useConfigComponent(props.component);
    const { entities } = useEntityContext();
    const entity = useMemo(() => {
        return entities === null || entities === void 0 ? void 0 : entities.find(e => { var _a; return Boolean(e.id) && e.id === ((_a = component.props) === null || _a === void 0 ? void 0 : _a.entityId); });
    }, [(_a = component.props) === null || _a === void 0 ? void 0 : _a.entityId, entities]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { fullWidth: true, variant: 'outlined', size: 'small', label: 'Name', value: component.name || '', 
                // InputProps={{ disableUnderline: true }}
                onChange: event => update('name', event.target.value) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { fullWidth: true, size: 'small', label: 'Title', variant: 'outlined', value: ((_b = component.props) === null || _b === void 0 ? void 0 : _b.title) || '', 
                // InputProps={{ disableUnderline: true }}
                onChange: event => update('props.title', event.target.value) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(EntityPicker, { fullWidth: true, value: ((_c = component.props) === null || _c === void 0 ? void 0 : _c.entityId) || '', filter: entity => Boolean(entity.id), onChange: value => {
                    if (value === null || value === void 0 ? void 0 : value.id) {
                        update('props.entityId', value.id);
                    }
                } })),
        entity && (React.createElement(React.Fragment, null,
            React.createElement(Typography, { variant: 'overline' }, "Add Required Fields:"),
            React.createElement(ConfigSortableList, { data: ((_d = component.props) === null || _d === void 0 ? void 0 : _d.formElements) || [], keyIndicator: item => `${item.type}-${item.key}-${item.dataSource}`, renderItem: item => (React.createElement(React.Fragment, null,
                    React.createElement(EntityColumnPicker, { entity: entity, disableClearable: true, value: item.dataSource || '' }))), footer: React.createElement(EntityColumnPicker, { entity: entity, value: '', disableClearable: true, onChange: value => {
                        var _a;
                        if (!value)
                            return;
                        const type = (() => {
                            switch (value === null || value === void 0 ? void 0 : value.type) {
                                case 'int':
                                case 'integer':
                                case 'numeric':
                                    return 'NumberField';
                                case 'enum':
                                    return 'Select';
                                default:
                                    return 'TextField';
                            }
                        })();
                        const formElement = {
                            type,
                            key: value.name,
                            title: value.displayName,
                            dataSource: value.name,
                            required: true
                        };
                        if (!((_a = component.props) === null || _a === void 0 ? void 0 : _a.formElements)) {
                            update('props.formElements', [formElement]);
                        }
                        else {
                            insert('props.formElements', formElement);
                        }
                    } }), onMove: (sourceIndex, destinationIndex) => {
                    move('props.formElements', sourceIndex, destinationIndex);
                }, onDelete: index => remove(`props.formElements[${index}]`) }))),
        React.createElement(Box, { mt: 1 },
            React.createElement(Typography, { variant: 'overline' }, "Redirect Path After Create:")),
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { fullWidth: true, size: 'small', label: 'Route Path', value: ((_e = component.props) === null || _e === void 0 ? void 0 : _e.routePath) || '', variant: 'outlined', 
                // InputProps={{ disableUnderline: true }}
                onChange: event => update('props.routePath', event.target.value), helperText: 'Example: /portal/risks/:id' })),
        React.createElement(Box, { mt: 1 },
            React.createElement(Typography, { variant: 'overline' }, "Events")),
        React.createElement(SiteEventConfigurator, { component: component, eventTypes: ['onDialogClose'] })));
};
export const SiteCreateEntityDialogPlugin = {
    name: 'Site Create Entity Dialog',
    type: 'create-entity-dialog',
    categories: ['dialog'],
    component: SiteCreateEntityDialog,
    configurator: SiteCreateEntityDialogConfigurator
};
//# sourceMappingURL=SiteCreateEntityDialogPlugin.js.map